import { Col, Row } from 'antd';
import { Pin } from 'types';
import { formatToDate } from 'utils/formatToDate';

import { DocumentDetailsModel } from 'api/documents/documents.model';
import { BasicCard } from 'components/BasicCard';
import CardRow from 'components/CardRow';
import CompositionRow from 'components/CompositionRow';
import Detail from 'components/Detail';
import Map from 'components/Map';
import { extractCoordinatesFromProduct } from 'components/Map/utils';
import SectionTitle from 'components/SectionTitle';
import SubTitle from 'components/SubTitle';
import useCertificateHelper from 'hooks/useCertificateHelper';
import {
  ADDITIONAL_DETAILS,
  CertificateDetailsModel,
  NESTED_DETAILS,
  PRODUCER_DETAILS,
  SIZE_CALCULATION_DETAILS,
} from 'models/certificate.model';
import { COMPANY } from 'models/company.model';

import RelatedCertificates from '../components/RelatedCertificates';
import RelatedDocuments from '../components/RelatedDocuments';
import ScoreCard from '../components/ScoreCard';
import ScoreDetail from '../components/ScoreDetail';
import Template, { ScoreContainer } from '../components/Template';

const BatteryPacks = ({
  additionalDetails = {} as CertificateDetailsModel,
  ...document
}: DocumentDetailsModel<CertificateDetailsModel>) => {
  const { getLabel, getValueByLabel, defaultValue } = useCertificateHelper();

  const pins: Pin[] = [];

  if (additionalDetails[ADDITIONAL_DETAILS.PRODUCER]) {
    const coordinates = extractCoordinatesFromProduct(
      additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
    );

    if (coordinates) {
      pins.push({
        coordinates,
        iconPath: '/pin-blue.svg',
      });
    }
  }

  return (
    <Template
      type={document.category}
      jobId={document.jobId}
      favourite={document.favourite}
      batchCard={
        <>
          <Detail
            label={getLabel('batteryPackSerialNumber(CertificateUID)')}
            value={additionalDetails.productId}
          />
          <Detail label={getLabel('passportId')} value={document.documentBlockchainAddress} />
          <Detail
            label={getLabel(ADDITIONAL_DETAILS.ASSEMBLY_NUMBER)}
            value={additionalDetails[ADDITIONAL_DETAILS.ASSEMBLY_NUMBER]}
          />
        </>
      }
      detailsCard={
        <CardRow>
          <Detail
            label={getLabel(NESTED_DETAILS.EVMODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.EVMODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCERS)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PRODUCERS,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_CELL_MODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCTION_DATE)}
            value={formatToDate(
              getValueByLabel(
                NESTED_DETAILS.BATTERY_PRODUCTION_DATE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(PRODUCER_DETAILS.COMPANY_FULL_NAME)}
            value={getValueByLabel(
              PRODUCER_DETAILS.COMPANY_FULL_NAME,
              additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
            )}
          />
        </CardRow>
      }
    >
      <RelatedDocuments
        history={document.history}
        category={document.category}
        company={COMPANY.BATTERY_PACKS}
        options={[
          { value: 'Car Manual', label: getLabel('carManual2') },
          { value: 'Description of Recycling', label: getLabel('descriptionOfRecycling2') },
        ]}
      />

      <RelatedCertificates
        additionalDetails={additionalDetails}
        relatedFields={['batteryCellsBatchNumber']}
        batteryPackSerialNumber={additionalDetails.productId}
      />

      <SectionTitle>{getLabel('scoring')}</SectionTitle>
      <BasicCard $justify="center">
        <ScoreContainer>
          <ScoreCard label={getLabel('productScore')}>
            <Detail label={getLabel('batteryPackBatchNo')} value={additionalDetails.productId} />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.BATTERY_PACK_CHILD_LABOR_AVERAGE)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.BATTERY_PACK_CHILD_LABOR_AVERAGE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.BATTERY_PACK_HUMAN_RIGHTS_AVERAGE)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.BATTERY_PACK_HUMAN_RIGHTS_AVERAGE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.BATTERY_PACK_ESG_SCORING_AVERAGE)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.BATTERY_PACK_ESG_SCORING_AVERAGE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
          </ScoreCard>
          <ScoreCard label={getLabel('batteryPackProducerScore')}>
            <Detail
              label={getLabel(PRODUCER_DETAILS.COMPANY)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY_FULL_NAME,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.CHILD_LABOR_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.CHILD_LABOR_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.ESG_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.ESG_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
          </ScoreCard>
          <ScoreCard label={getLabel('batteryCellsScore')}>
            <Detail
              label={getLabel('cellsBatchNumber')}
              value={getValueByLabel(
                NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.BATTERY_CELLS_CHILD_LABOR_AVERAGE)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.BATTERY_CELLS_CHILD_LABOR_AVERAGE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.BATTERY_CELLS_HUMAN_RIGHTS_AVERAGE)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.BATTERY_CELLS_HUMAN_RIGHTS_AVERAGE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.BATTERY_CELL_PRODUCER_ESG_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.BATTERY_CELL_PRODUCER_ESG_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
          </ScoreCard>
        </ScoreContainer>
      </BasicCard>

      <SectionTitle>{getLabel('specification')}</SectionTitle>
      <Row gutter={[16, 16]}>
        <Col md={24} lg={12}>
          <SubTitle>{getLabel('generalInfo')}</SubTitle>
          <CompositionRow<NESTED_DETAILS>
            composition={additionalDetails?.[ADDITIONAL_DETAILS.DETAILS]}
            labels={[
              NESTED_DETAILS.WEIGHT,
              NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY,
              NESTED_DETAILS.COOLING,
              NESTED_DETAILS.CELLS_SETUP,
              NESTED_DETAILS.TOTAL_CAPACITY,
              NESTED_DETAILS.USABLE_CAPACITY,
              NESTED_DETAILS.STORAGE_TEMP_RANGE,
            ]}
          />
          <SubTitle>{getLabel(NESTED_DETAILS.DESCRIPTION)}</SubTitle>
          <BasicCard>
            {getValueByLabel(
              NESTED_DETAILS.DESCRIPTION,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS],
              defaultValue
            )}
          </BasicCard>
        </Col>
        <Col md={24} lg={12}>
          <SubTitle>{getLabel('relevantNumbers')}</SubTitle>
          <CompositionRow<SIZE_CALCULATION_DETAILS>
            composition={additionalDetails?.[ADDITIONAL_DETAILS.SIZE_CALCULATION]}
            labels={Object.values(SIZE_CALCULATION_DETAILS)}
          />
          <SubTitle>{getLabel(NESTED_DETAILS.WARRANTY_CONDITIONS)}</SubTitle>
          <BasicCard>
            {getValueByLabel(
              NESTED_DETAILS.WARRANTY_CONDITIONS,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS],
              defaultValue
            )}
          </BasicCard>
        </Col>
      </Row>

      <SectionTitle>{getLabel('batteryPackProducer')}</SectionTitle>
      <Map
        pins={pins}
        card={
          <BasicCard>
            <SectionTitle noMargin dividerMargin="1rem">
              {getLabel('producer')}
            </SectionTitle>
            <Detail
              label={getLabel(PRODUCER_DETAILS.COMPANY_FULL_NAME)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY_FULL_NAME,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.COMPANY_HEADQUATERS)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY_HEADQUATERS,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.FACTORY_LOCATION)}
              value={getValueByLabel(
                PRODUCER_DETAILS.FACTORY_LOCATION,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.COORDINATES)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COORDINATES,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
          </BasicCard>
        }
      />
    </Template>
  );
};

export default BatteryPacks;
